import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerAuthContext = createContext(null);

export function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null); // null=loading, false=unauth, object=auth
  const [favoriteSlugs, setFavoriteSlugs] = useState(new Set());

  const loadFavorites = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/favorites`, { withCredentials: true });
      setFavoriteSlugs(new Set(data.map((f) => f.project.slug)));
    } catch (err) {
      console.warn("Favorites load failed:", err?.response?.status ?? err?.message);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // CustomerAuthCallback handles the exchange and sets state directly.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/customer/me`, { withCredentials: true });
        if (!cancelled) {
          setCustomer(data);
          loadFavorites();
        }
      } catch (err) {
        if (!cancelled) setCustomer(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadFavorites]);

  // Called by CustomerAuthCallback right after a successful session exchange.
  const setAuthenticated = useCallback((data) => {
    setCustomer(data);
    loadFavorites();
  }, [loadFavorites]);

  const loginWithGoogle = useCallback((redirectPath) => {
    const target = redirectPath || window.location.pathname;
    const redirectUrl = window.location.origin + target;
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/customer/logout`, null, { withCredentials: true });
    } catch (err) {
      console.warn("Customer logout failed:", err?.response?.status ?? err?.message);
    }
    setCustomer(false);
    setFavoriteSlugs(new Set());
  }, []);

  const toggleFavorite = useCallback(async (slug) => {
    if (!customer) {
      loginWithGoogle();
      return;
    }
    const isFav = favoriteSlugs.has(slug);
    setFavoriteSlugs((s) => {
      const next = new Set(s);
      if (isFav) next.delete(slug);
      else next.add(slug);
      return next;
    });
    try {
      if (isFav) {
        await axios.delete(`${API}/favorites/${slug}`, { withCredentials: true });
      } else {
        await axios.post(`${API}/favorites/${slug}`, null, { withCredentials: true });
      }
    } catch (err) {
      console.warn("Toggle favorite failed:", err?.response?.status ?? err?.message);
      loadFavorites();
    }
  }, [customer, favoriteSlugs, loginWithGoogle, loadFavorites]);

  const value = useMemo(
    () => ({ customer, favoriteSlugs, loginWithGoogle, logout, toggleFavorite, setAuthenticated, loadFavorites }),
    [customer, favoriteSlugs, loginWithGoogle, logout, toggleFavorite, setAuthenticated, loadFavorites]
  );

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export const useCustomerAuth = () => useContext(CustomerAuthContext);
